import { useCallback, useRef, useState } from 'react';
import { InitPhase } from './types';

export interface InitIdentity {
	id: string;
	email: string;
	defaultSite: string;
	defaultSiteProjectId: string;
	defaultTeamId: string;
	entitlements: string[];
}

export interface InitializationSteps {
	awaitConfig: () => Promise<void>;
	awaitToken: () => Promise<string>;
	runIdentityStep: (accessToken: string) => Promise<InitIdentity>;
	runFlagsStep: (identity: InitIdentity) => Promise<void>;
	runDataStep: (identity: InitIdentity) => Promise<void>;
}

export interface UseInitializationResult {
	init: () => Promise<void>;
	phase: InitPhase;
	error: unknown;
}

/**
 * Orchestrates application initialization as one ordered async sequence.
 * Pure control flow: no Redux for sequencing. The provided steps dispatch
 * resolved domain data to Redux as they complete.
 */
export const useInitialization = (
	steps: InitializationSteps,
): UseInitializationResult => {
	const [phase, setPhase] = useState<InitPhase>('config');
	const [error, setError] = useState<unknown>(null);
	const startedRef = useRef(false);

	const init = useCallback(async () => {
		if (startedRef.current) return;
		startedRef.current = true;
		try {
			setPhase('config');
			await steps.awaitConfig();

			setPhase('auth');
			const accessToken = await steps.awaitToken();

			setPhase('identity');
			const identity = await steps.runIdentityStep(accessToken);

			setPhase('flags');
			await steps.runFlagsStep(identity);

			setPhase('data');
			await steps.runDataStep(identity);
		} catch (e) {
			setError(e);
			setPhase('error');
		}
		// Note: transition to 'ready' is owned by LoadingProvider, which maps
		// 'ready' to the existing loading-gate condition (Task 5).
	}, [steps]);

	return { init, phase, error };
};
